import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import { MapPin } from 'lucide-react';
import { useSelectedMetrics, isMetricSelected, getCpaClassName } from '../utils/metricsUtils';

/**
 * Composant modulaire pour afficher les emplacements Performance Max
 */
const PerformanceMaxPlacementsTableModular = React.memo(({
  selectedAccount,
  selectedPeriod,
  customStartDate,
  customEndDate,
  formatNumber,
  isVisible = true,
  onToggleVisibility,
  token,
  setError,
  apiEndpoint = '/api/dashboard/components/performance-max-placements',
  selectedCampaignFilter,
  pmaxLabelsFilter,
  campaignLabelsMap,
  performanceMaxChannelsData,
  moduleId = 'pmax_placements'
}) => {
  const selectedMetrics = useSelectedMetrics(selectedAccount?.id || 'global');

  // États locaux pour les données
  const [placementsData, setPlacementsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // Calculer les IDs des campagnes filtrées
  const getFilteredCampaignIds = () => {
    if (!selectedCampaignFilter && (!pmaxLabelsFilter || pmaxLabelsFilter.length === 0)) {
      return null; // Pas de filtre actif
    }

    if (!performanceMaxChannelsData || performanceMaxChannelsData.length === 0) {
      return null;
    }

    const filteredIds = performanceMaxChannelsData
      .filter(campaign => {
        const campaignId = (campaign.id || campaign.campaignId || '').toString();
        const campaignName = campaign.name || campaign.campaignName || '';

        // Filtre par campagne spécifique
        if (selectedCampaignFilter) {
          const filterId = (selectedCampaignFilter.id || selectedCampaignFilter.campaignId || '').toString();
          const filterName = selectedCampaignFilter.name || selectedCampaignFilter.campaignName || '';
          if (campaignId !== filterId && campaignName !== filterName) {
            return false;
          }
        }

        // Filtre par labels
        if (pmaxLabelsFilter && pmaxLabelsFilter.length > 0) {
          const labels = (campaignLabelsMap && campaignLabelsMap[campaignId]) || [];
          if (!pmaxLabelsFilter.some(label => labels.includes(label))) {
            return false;
          }
        }

        return true;
      })
      .map(c => c.id || c.campaignId)
      .filter(Boolean);

    return filteredIds.length > 0 ? filteredIds : null;
  };

  // Charger les données quand le composant est monté ou que les dépendances changent
  useEffect(() => {
    if (!selectedAccount || !selectedPeriod || !token) return;

    const loadPlacements = async () => {
      try {
        setLoading(true);
        const filteredCampaignIds = getFilteredCampaignIds();
        console.log('🎯 [PERFORMANCE-MAX-PLACEMENTS-MODULAR] Chargement des emplacements Performance Max...',
          filteredCampaignIds ? `(${filteredCampaignIds.length} campagnes filtrées)` : '');

        const requestBody = {
          sources: [selectedAccount.id],
          period: selectedPeriod
        };

        // Ajouter les dates personnalisées si période CUSTOM
        if (selectedPeriod === 'CUSTOM') {
          if (!customStartDate || !customEndDate) return;
          requestBody.customStartDate = customStartDate;
          requestBody.customEndDate = customEndDate;
        }

        // Ajouter les campaignIds filtrés si un filtre est actif
        if (filteredCampaignIds) {
          requestBody.campaignIds = filteredCampaignIds;
        }

        console.log('🎯 [PERFORMANCE-MAX-PLACEMENTS-MODULAR] Requête vers:', `${config.API_URL}${apiEndpoint}`);
        console.log('🎯 [PERFORMANCE-MAX-PLACEMENTS-MODULAR] Payload:', requestBody);

        const response = await axios.post(
          `${config.API_URL}${apiEndpoint}`,
          requestBody,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        console.log('🎯 [PERFORMANCE-MAX-PLACEMENTS-MODULAR] Réponse reçue:', response.data);

        if (response.data.success && response.data.data) {
          setPlacementsData(response.data.data);
          setIsDataLoaded(true);
          console.log('✅ [PERFORMANCE-MAX-PLACEMENTS-MODULAR] Données chargées:', {
            totalPlacements: response.data.data.totalPlacements,
            uniquePlacements: response.data.data.uniquePlacementsCount,
            period: selectedPeriod
          });
        } else {
          console.error('❌ [PERFORMANCE-MAX-PLACEMENTS-MODULAR] API retourne success=false:', response.data);
          setError('Erreur lors du chargement des emplacements Performance Max');
        }
      } catch (error) {
        console.error('❌ [PERFORMANCE-MAX-PLACEMENTS-MODULAR] Erreur chargement:', error);
        console.error('❌ [PERFORMANCE-MAX-PLACEMENTS-MODULAR] Détails:', error.response?.data || error.message);
        setError('Erreur lors du chargement des emplacements Performance Max');
      } finally {
        setLoading(false);
      }
    };

    loadPlacements();
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, token, setError, selectedCampaignFilter, pmaxLabelsFilter, performanceMaxChannelsData?.length]);

  // Réinitialiser la page quand les données changent
  useEffect(() => {
    setCurrentPage(1);
  }, [placementsData]);

  // Ne pas afficher si pas de données et pas en chargement ET qu'on n'a jamais essayé de charger
  if (!loading && !isDataLoaded && !selectedAccount) return null;

  // Fonction pour obtenir l'icône selon le type d'emplacement
  const getPlacementIcon = (type) => {
    switch (type) {
      case 'Site Web': return '🌐';
      case 'Application': return '📱';
      case 'Application Mobile': return '📱';
      case 'Vidéo YouTube': return '📹';
      case 'Chaîne YouTube': return '📺';
      case 'YouTube': return '📺';
      case 'Gmail': return '📧';
      case 'Discover': return '🔍';
      case 'Réseau Display': return '🖼️';
      default: return '📍';
    }
  };

  // Fonction pour générer l'URL d'un emplacement
  const getPlacementUrl = (placement) => {
    const placementValue = placement.placement;
    
    // Si c'est Google Owned & Operated, pas d'URL
    if (placementValue === 'Google Owned & Operated') {
      return '#';
    }
    
    // Déterminer le type d'URL selon le type d'emplacement
    switch (placement.placementType) {
      case 'Site Web':
        // Si c'est déjà une URL complète
        if (placementValue.startsWith('http://') || placementValue.startsWith('https://')) {
          return placementValue;
        }
        // Sinon, ajouter https://
        return `https://${placementValue}`;
        
      case 'Vidéo YouTube':
        // Si c'est une URL YouTube complète
        if (placementValue.includes('youtube.com') || placementValue.includes('youtu.be')) {
          return placementValue;
        }
        // Si c'est un ID de vidéo YouTube (généralement 11 caractères alphanumériques)
        if (/^[a-zA-Z0-9_-]{10,12}$/.test(placementValue)) {
          return `https://www.youtube.com/watch?v=${placementValue}`;
        }
        // Sinon, faire une recherche
        return `https://www.youtube.com/results?search_query=${encodeURIComponent(placementValue)}`;
        
      case 'Chaîne YouTube':
      case 'YouTube':
        // Si c'est une URL YouTube complète
        if (placementValue.includes('youtube.com')) {
          return placementValue;
        }
        // Si c'est un ID de chaîne
        if (placementValue.startsWith('UC') || placementValue.startsWith('channel/')) {
          return `https://www.youtube.com/channel/${placementValue.replace('channel/', '')}`;
        }
        // Si c'est un ID de vidéo YouTube (pour le type 'YouTube' générique)
        if (/^[a-zA-Z0-9_-]{10,12}$/.test(placementValue)) {
          return `https://www.youtube.com/watch?v=${placementValue}`;
        }
        // Sinon, recherche YouTube
        return `https://www.youtube.com/results?search_query=${encodeURIComponent(placementValue)}`;
        
      case 'Application':
      case 'Application Mobile':
        // Pour les applications, faire une recherche Google Play
        return `https://play.google.com/store/search?q=${encodeURIComponent(placement.displayName || placementValue)}&c=apps`;
        
      default:
        // Par défaut, considérer comme un site web
        if (placementValue.startsWith('http://') || placementValue.startsWith('https://')) {
          return placementValue;
        }
        return `https://${placementValue}`;
    }
  };

  return (
    <div className="placements-table-section">
      <div className="section-header">
        <h4><MapPin size={16} />&nbsp;Performance Max - Emplacements {placementsData && placementsData.placements ? `(${placementsData.placements.length})` : ''}</h4>
        <button 
          onClick={onToggleVisibility}
          className="section-toggle-btn"
          title={isVisible ? "Masquer la section" : "Afficher la section"}
        >
          {isVisible ? '−' : '+'}
        </button>
      </div>

      {isVisible && (
        <>
          {loading ? (
            <div className="loading-message" style={{ textAlign: 'center', padding: '40px' }}>
              <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
              Chargement des emplacements Performance Max...<br /><br />
            </div>
          ) : placementsData && placementsData.placements && placementsData.placements.length > 0 ? (
            <>

              {/* Tableau des emplacements */}
              <div className="campaigns-table-wrapper">
                <table className="campaigns-table" key={selectedMetrics.join(",")} style={{ tableLayout: "auto" }}>
                <thead>
                  <tr>
                    <th style={{ width: '35%' }}>Emplacement</th>
                    <th>Type</th>
                    {isMetricSelected('impressions', selectedAccount?.id || 'global') && (
                      <th className="numeric">Impressions</th>
                    )}
                    {isMetricSelected('impressions', selectedAccount?.id || 'global') && (
                      <th className="numeric">% du total</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const startIndex = (currentPage - 1) * itemsPerPage;
                    const endIndex = startIndex + itemsPerPage;
                    const paginatedPlacements = placementsData.placements.slice(startIndex, endIndex);
                    
                    return paginatedPlacements.map((placement, index) => {
                      const percentage = (placement.impressions / placementsData.totalImpressions * 100).toFixed(2);
                    
                    return (
                      <tr key={`${placement.placement}-${index}`}>
                        <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {placement.placement === 'Google Owned & Operated' ? (
                            <span title={placement.placement} style={{ color: '#6b7280' }}>
                              {placement.displayName || placement.placement}
                            </span>
                          ) : (
                            <a 
                              href={getPlacementUrl(placement)}
                              target="_blank" 
                              rel="noopener noreferrer"
                              title={placement.placement}
                              style={{ 
                                color: '#0284c7',
                                textDecoration: 'none',
                                cursor: 'pointer'
                              }}
                              onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                              onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                            >
                              {placement.displayName || placement.placement}
                            </a>
                          )}
                        </td>
                        <td>
                          <span style={{ 
                            display: 'inline-block',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '500',
                            backgroundColor: getPlacementTypeColor(placement.placementType),
                            color: '#374151'
                          }}>
                            {getPlacementIcon(placement.placementType)} {placement.placementType}
                          </span>
                        </td>
                        {isMetricSelected('impressions', selectedAccount?.id || 'global') && (
                          <td className="numeric">{formatNumber(placement.impressions)}</td>
                        )}
                        {isMetricSelected('impressions', selectedAccount?.id || 'global') && (
                          <td className="numeric">{percentage}%</td>
                        )}
                      </tr>
                    );
                    });
                  })()}
                </tbody>
                </table>
              </div>
              
              {/* Contrôles de pagination */}
              {Math.ceil(placementsData.placements.length / itemsPerPage) > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-info">
                    <span>
                      Affichage {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, placementsData.placements.length)} sur {placementsData.placements.length} emplacements
                    </span>
                  </div>
                  <div className="pagination-buttons">
                    <button 
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      ← Précédent
                    </button>
                    <span className="page-numbers">
                      Page {currentPage} sur {Math.ceil(placementsData.placements.length / itemsPerPage)}
                    </span>
                    <button 
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === Math.ceil(placementsData.placements.length / itemsPerPage)}
                      className="pagination-btn"
                    >
                      Suivant →
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
              Aucun emplacement Performance Max trouvé pour la période sélectionnée
            </div>
          )}
        </>
      )}
    </div>
  );
});

// Fonction helper pour obtenir la couleur de fond selon le type d'emplacement
function getPlacementTypeColor(type) {
  switch (type) {
    case 'Site Web': return '#E0F2FE';
    case 'Application': return '#F3E8FF';
    case 'Application Mobile': return '#F3E8FF';
    case 'Vidéo YouTube': return '#FEE2E2';
    case 'Chaîne YouTube': return '#FEE2E2';
    case 'YouTube': return '#FEE2E2';
    case 'Gmail': return '#FEF3C7';
    case 'Discover': return '#D1FAE5';
    case 'Réseau Display': return '#E0E7FF';
    default: return '#F3F4F6';
  }
}

PerformanceMaxPlacementsTableModular.displayName = 'PerformanceMaxPlacementsTableModular';

export default PerformanceMaxPlacementsTableModular;